/**
 * Sensitive changes go through a second person. The request carries everything needed to
 * apply the change later, so the approver decides on exactly what was asked.
 */
export const APPROVAL_KINDS = [
  'management.grant',
  'superadmin.appoint',
  'superadmin.remove',
  'tenant.suspend',
  'tenant.domains',
  'tenant.sso',
  'staff.owner',
] as const;
export type ApprovalKind = (typeof APPROVAL_KINDS)[number];

export const APPROVAL_LABELS: Record<ApprovalKind, string> = {
  'management.grant': 'Grant management access',
  'superadmin.appoint': 'Appoint a superadmin',
  'superadmin.remove': 'Remove a superadmin',
  'tenant.suspend': 'Suspend the tenant',
  'tenant.domains': 'Change allowed email domains',
  'tenant.sso': 'Change single sign-on',
  'staff.owner': 'Make a Platform Owner',
};

export type ApprovalStatus = 'pending' | 'approved' | 'rejected' | 'expired';

export interface ApprovalRequest {
  id: string;
  /** `tenant`: decided by another superadmin or DinoCloud. `dinocloud`: by another Platform Owner. */
  scope: 'tenant' | 'dinocloud';
  /** The tenant the change is about, or `_dinocloud` for staff-side changes. */
  tenantId: string;
  kind: ApprovalKind;
  /** What will happen, in one sentence. Shown to the approver. */
  summary: string;
  payload: Record<string, unknown>;
  requestedBy: string;
  requestedByName: string;
  requestedAt: string;
  expiresAt: string;
  status: ApprovalStatus;
  decidedBy?: string;
  decidedAt?: string;
  note?: string;
}

/** Requests expire after 72 hours. */
export const APPROVAL_TTL_MS = 72 * 60 * 60 * 1000;
